<template>
  <v-form v-if="agreement" ref="form" v-model="valid" lazy-validation @submit.prevent="onSubmit">
    <v-alert v-if="activationError" :value="true" color="error" icon="warning" class="mb-5" dark>{{
      $t('message.activation_overlap_error')
    }}</v-alert>

    <v-row no-gutters justify="center">
      <v-alert
        v-if="agreement.networkPlatform.scheduleAnticipation >= 0"
        type="info"
        :value="true"
        class="mb-5"
        style="max-width: 600px;"
      >
        {{
          $tc('message.activation_schedule_anticipation_info', agreement.networkPlatform.scheduleAnticipation, {
            date: $moment(minStartDate).format('LL')
          })
        }}
      </v-alert>
    </v-row>

    <template v-if="agreement.servicePlan.allowedFixedTerms.length > 0">
      <v-row justify="center" class="mt-3">
        <v-col style="max-width: 400px">
          <v-menu v-model="startDateMenu" :close-on-content-click="false" max-width="290">
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="formattedStartDate"
                :label="$t('message.start_date')"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              :locale="$i18n.locale"
              :first-day-of-week="'es' === $i18n.locale ? 1 : 0"
              :min="minStartDate"
              :max="maxStartDate"
              :allowed-dates="allowedDates"
              @change="
                startDateMenu = false
                dateBetweenPeriods()
              "
            ></v-date-picker>
          </v-menu>

          <template v-if="agreement.servicePlan.allowedFixedTerms.length > 1">
            <div class="body-2 mt-3">{{ $t('message.fixed_term_label') }}</div>
            <v-radio-group v-model="fixedTerm" @change="dateBetweenPeriods()">
              <v-radio
                v-for="allowedfixedTerm in agreement.servicePlan.allowedFixedTerms"
                :key="allowedfixedTerm"
                :label="$t('message.fixed_term_' + allowedfixedTerm)"
                :value="allowedfixedTerm"
              ></v-radio>
            </v-radio-group>
          </template>

          <v-alert
            v-if="startDate && endDate"
            color="grey lighten-5"
            :value="true"
            v-html="
              $t('message.your_connection_will_be_active_from_to', {
                startDate: $moment(startDate).format('LL'),
                endDate: $moment(endDate).format('LL')
              })
            "
          >
          </v-alert>
        </v-col>
      </v-row>
    </template>
    <v-row
      v-else-if="$vuetify.breakpoint.mdAndUp && 'day' === agreement.servicePlan.granularity"
      justify="center"
      class="mt-3"
    >
      <div class="text-center">
        <div>
          <v-label>{{ $t('message.start_date') }}</v-label>
        </div>
        <v-date-picker
          v-model="startDate"
          class="ma-3"
          :locale="$i18n.locale"
          :first-day-of-week="'es' === $i18n.locale ? 1 : 0"
          :min="minStartDate"
          :max="maxStartDate"
          :events="eventDates"
          event-color="blue"
          :allowed-dates="allowedDates"
          @change="
            startDateMenu = false
            dateBetweenPeriods()
          "
        ></v-date-picker>
      </div>

      <div class="text-center">
        <div>
          <v-label>{{ $t('message.end_date') }}</v-label>
        </div>
        <v-date-picker
          v-model="endDate"
          class="ma-3"
          :locale="$i18n.locale"
          :first-day-of-week="'es' === $i18n.locale ? 1 : 0"
          :min="minEndDate"
          :max="maxEndDate"
          :events="eventDates"
          event-color="blue"
          :allowed-dates="allowedDates"
          @change="
            endDateMenu = false
            dateBetweenPeriods()
          "
        ></v-date-picker>
      </div>
    </v-row>
    <template v-else>
      <v-row justify="center" class="mt-3">
        <v-col style="max-width: 400px">
          <v-alert
            v-if="'day' !== agreement.servicePlan.granularity"
            :value="true"
            color="info"
            icon="info"
            class="mb-5"
            dark
          >
            <template v-if="'month' === agreement.servicePlan.granularity">
              {{ $t('message.request_activation_monthly_basis') }}
            </template>
            <template v-if="'quarter' === agreement.servicePlan.granularity">
              {{ $t('message.request_activation_quarterly_basis') }}
            </template>
            <template v-if="'semester' === agreement.servicePlan.granularity">
              {{ $t('message.request_activation_half_yearly_basis') }}
            </template>
            <template v-if="'year' === agreement.servicePlan.granularity">
              {{ $t('message.request_activation_yearly_basis') }}
            </template>
          </v-alert>

          <div class="title mb-6">
            {{ $t('message.select_activation_days') }}
          </div>
          <v-menu v-model="startDateMenu" :close-on-content-click="false" max-width="290">
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="formattedStartDate"
                :label="$t('message.start_date')"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              :locale="$i18n.locale"
              :first-day-of-week="'es' === $i18n.locale ? 1 : 0"
              :min="minStartDate"
              :max="maxStartDate"
              :allowed-dates="allowedDates"
              @change="
                startDateMenu = false
                dateBetweenPeriods()
              "
            ></v-date-picker>
          </v-menu>

          <v-text-field
            v-if="'month' === agreement.servicePlan.granularity"
            v-model="granularityAmount"
            type="number"
            :label="$t('message.amount')"
            :suffix="$t('message.months')"
            :rules="granularityRules.month"
          ></v-text-field>

          <v-text-field
            v-else-if="'quarter' === agreement.servicePlan.granularity"
            v-model="granularityAmount"
            type="number"
            :label="$t('message.amount')"
            :suffix="$t('message.quarters')"
            :rules="granularityRules.quarter"
          ></v-text-field>

          <v-text-field
            v-else-if="'semester' === agreement.servicePlan.granularity"
            v-model="granularityAmount"
            type="number"
            :label="$t('message.amount')"
            :suffix="$t('message.semesters')"
            :rules="granularityRules.semester"
          ></v-text-field>

          <v-text-field
            v-else-if="'year' === agreement.servicePlan.granularity"
            v-model="granularityAmount"
            type="number"
            :label="$t('message.amount')"
            :suffix="$t('message.years')"
            :rules="granularityRules.year"
          ></v-text-field>

          <v-menu v-else v-model="endDateMenu" :close-on-content-click="false" max-width="290">
            <template v-slot:activator="{ on }">
              <v-text-field :value="formattedEndDate" :label="$t('message.end_date')" readonly v-on="on"></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              :locale="$i18n.locale"
              :first-day-of-week="'es' === $i18n.locale ? 1 : 0"
              :min="minEndDate"
              :max="maxEndDate"
              :allowed-dates="allowedDates"
              @change="
                endDateMenu = false
                dateBetweenPeriods()
              "
            ></v-date-picker>
          </v-menu>

          <v-alert
            v-if="startDate && endDate && 'day' !== agreement.servicePlan.granularity"
            color="grey lighten-5"
            :value="true"
            v-html="
              $t('message.your_connection_will_be_active_from_to', {
                startDate: $moment(startDate).format('LL'),
                endDate: $moment(endDate).format('LL')
              })
            "
          >
          </v-alert>
        </v-col>
      </v-row>
    </template>

    <v-row justify="center">
      <v-col
        :style="{
          maxWidth: 'day' === agreement.servicePlan.granularity ? '628px' : '400px'
        }"
      >
        <template v-if="airtimeBalancesMerged.length > 0">
          <div v-for="airtimeBalance in airtimeBalancesMerged" :key="airtimeBalance.airtimePlan['@id']">
            <v-alert
              v-if="!initialAirtimePlan"
              :value="true"
              class="grey lighten-5"
              border="left"
              colored-border
              :type="isBalanceExceeded(airtimeBalance) ? 'error' : 'info'"
            >
              <div
                v-html="
                  $t('message.remaining_airtime_balance_info', {
                    remainingDays: airtimeBalance.remainingDays,
                    airtimePlanName: airtimeBalance.airtimePlan.name
                  })
                "
              ></div>
              <div v-if="isBalanceExceeded(airtimeBalance)" class="red--text mt-3">
                {{ $t('message.remaining_airtime_balance_insufficient_info') }}
              </div>
            </v-alert>

            <v-alert
              v-else-if="
                initialAirtimePlan &&
                  isBalanceExceeded(airtimeBalance) &&
                  initialAirtimePlan['@id'] === airtimeBalance.airtimePlan['@id']
              "
              type="error"
            >
              <div
                v-html="
                  $t('message.remaining_airtime_balance_exceeded_info', {
                    remainingDays: airtimeBalance.remainingDays
                  })
                "
              ></div>
            </v-alert>
          </div>
        </template>
        <div class="text-center">
          <v-btn class="ma-3" @click="$emit('close')">{{ $t('message.cancel') }} </v-btn>
          <v-btn
            :disabled="!startDate || !endDate || !valid || !availableDays || isInitialAirtimePlanExceeded"
            color="primary"
            class="ma-3"
            type="submit"
            >{{ $t('message.continue') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-form>
</template>
<script>
export default {
  name: 'ActivationDays',
  props: {
    agreement: {
      type: Object,
      required: true
    },
    item: {
      type: Object,
      required: true
    },
    airtimeBalancesMerged: {
      type: Array,
      required: false,
      default: null
    },
    initialAirtimePlan: {
      type: Object,
      required: false,
      default: () => null
    }
  },

  data() {
    return {
      activationError: false,
      unavailableDays: [],
      availableDays: true,
      startDate: null,
      startDateMenu: false,
      endDate: null,
      endDateMenu: false,
      granularityAmount: 1,
      fixedTerm: null,
      granularityRules: {
        month: [
          v => (!!v && v > 0) || this.$t('message.this_field_is_required'),
          v => (v > 0 && v <= 60) || this.$t('message.granularity_max_value', { max: 60 })
        ],
        quarter: [
          v => (!!v && v > 0) || this.$t('message.this_field_is_required'),
          v => (v > 0 && v <= 20) || this.$t('message.granularity_max_value', { max: 20 })
        ],
        semester: [
          v => (!!v && v > 0) || this.$t('message.this_field_is_required'),
          v => (v > 0 && v <= 10) || this.$t('message.granularity_max_value', { max: 10 })
        ],
        year: [
          v => (!!v && v > 0) || this.$t('message.this_field_is_required'),
          v => (v > 0 && v <= 5) || this.$t('message.granularity_max_value', { max: 5 })
        ]
      },
      minEndDate: this.$moment()
        .toISOString()
        .substr(0, 10),
      valid: true,
      isExceeded: false
    }
  },

  computed: {
    minStartDate() {
      if (!this.agreement) {
        return
      }
      let date = this.$moment()
        .tz('Europe/Madrid')
        .add(this.agreement.networkPlatform.scheduleAnticipation, 'days')

      return date.toISOString().substr(0, 10)
    },

    eventDates() {
      let eventDates = []
      if (!this.startDate || !this.endDate) {
        return eventDates
      }

      let dt = new Date()
      for (eventDates, dt = new Date(this.startDate); dt <= new Date(this.endDate); dt.setDate(dt.getDate() + 1)) {
        eventDates.push(new Date(dt).toISOString().substr(0, 10))
      }

      return eventDates
    },

    formattedStartDate() {
      if (!this.startDate) {
        return
      }

      let date = new Date(this.startDate).toISOString().substr(0, 10) + ' 08:00:00'
      return this.$moment.tz(date, 'Europe/Madrid').format('LL')
    },

    formattedEndDate() {
      if (!this.endDate) {
        return
      }

      let date = new Date(this.endDate).toISOString().substr(0, 10) + ' 08:00:00'
      return this.$moment.tz(date, 'Europe/Madrid').format('LL')
    },

    maxStartDate() {
      let maxStartDate = this.$moment()
        .add(1, 'years')
        .toISOString()
        .substr(0, 10)

      if (this.agreement && this.agreement.endsAt) {
        maxStartDate = this.$moment(this.agreement.endsAt)
          .toISOString()
          .substr(0, 10)
      }

      return maxStartDate
    },

    maxEndDate() {
      let maxEndDate = this.$moment()
        .add(1, 'years')
        .toISOString()
        .substr(0, 10)

      if (this.agreement && this.agreement.endsAt) {
        maxEndDate = this.$moment(this.agreement.endsAt)
          .toISOString()
          .substr(0, 10)
      }
      return maxEndDate
    },

    granularityEndDate() {
      if (
        !this.granularityAmount ||
        !this.startDate ||
        !this.agreement ||
        'day' === this.agreement.servicePlan.granularity
      ) {
        return null
      }

      if ('semester' === this.agreement.servicePlan.granularity) {
        return this.$moment(this.startDate)
          .add(this.granularityAmount * 6, 'month')
          .subtract(1, 'days')
          .format('YYYY-MM-DD')
      } else {
        return this.$moment(this.startDate)
          .add(this.granularityAmount, this.agreement.servicePlan.granularity)
          .subtract(1, 'days')
          .format('YYYY-MM-DD')
      }
    },

    fixedTermEndDate() {
      if (!this.fixedTerm || !this.startDate) {
        return null
      }
      if ('quarter' === this.fixedTerm) {
        return this.$moment(this.startDate)
          .add(3, 'month')
          .subtract(1, 'days')
          .endOf('month')
          .add(1, 'days')
          .format('YYYY-MM-DD')
      } else {
        return this.$moment(this.startDate)
          .add(1, this.fixedTerm)
          .subtract(1, 'days')
          .endOf('month')
          .format('YYYY-MM-DD')
      }
    },

    isInitialAirtimePlanExceeded() {
      if (this.airtimeBalancesMerged.length < 1) {
        return
      }

      let isExceeded

      this.airtimeBalancesMerged.forEach(airtimeBalance => {
        if (this.initialAirtimePlan && this.initialAirtimePlan['@id'] === airtimeBalance.airtimePlan['@id']) {
          isExceeded =
            this.$moment(this.endDate).diff(this.$moment(this.startDate), 'days') >= airtimeBalance.remainingDays
        }
      })

      return isExceeded
    }
  },

  watch: {
    item: {
      handler: function(val) {
        this.startDate = new Date(val.startsAt).toISOString().substr(0, 10)

        this.endDate = new Date(val.endsAt).toISOString().substr(0, 10)
      },
      deep: true
    },

    granularityEndDate(granularityEndDate) {
      this.endDate = granularityEndDate
    },

    fixedTermEndDate(fixedTermEndDate) {
      this.endDate = fixedTermEndDate
      this.dateBetweenPeriods()
    },

    startDate(startDate) {
      this.minEndDate = startDate

      if (startDate > this.endDate) {
        this.endDate = null
      }
    }
  },

  created() {
    this.getUnavailableDays(this.agreement.periods)
    if (this.item.startsAt) {
      this.startDate = this.$moment(this.item.startsAt).format('YYYY-MM-DD')
    }

    if (this.item.endsAt) {
      this.endDate = this.$moment(this.item.endsAt)
        .subtract(1, 'days')
        .format('YYYY-MM-DD')
    }

    if (this.agreement.servicePlan.allowedFixedTerms && this.agreement.servicePlan.allowedFixedTerms.length === 1) {
      this.fixedTerm = this.agreement.servicePlan.allowedFixedTerms[0]
    }
  },

  mounted() {
    this.minEndDate = this.minStartDate
    this.startDate = null
    this.endDate = null
  },

  methods: {
    dateBetweenPeriods() {
      if (!this.endDate) {
        this.endDate = this.fixedTermEndDate
      }
      this.availableDays = true
      this.activationError = false
      this.unavailableDays.forEach(day => {
        if (day >= this.startDate && day <= this.endDate) {
          this.availableDays = false
          this.activationError = true
        }
      })
    },
    allowedDates(val) {
      return this.unavailableDays.indexOf(val) === -1
    },
    getUnavailableDays(periods) {
      periods.forEach(period => {
        let startDay = this.$moment(period.startsAt)
        let endDay = this.$moment(period.endsAt)

        while (startDay.isBefore(endDay)) {
          this.unavailableDays.push(this.$moment(startDay).format('YYYY-MM-DD'))
          startDay.add(1, 'days')
        }
      })
    },
    isBalanceExceeded(airtimeBalance) {
      if (!airtimeBalance) {
        return
      }

      return this.$moment(this.endDate).diff(this.$moment(this.startDate), 'days') >= airtimeBalance.remainingDays
    },

    onSubmit() {
      let item = { ...this.item }
      item.startsAt = this.startDate + ' 08:00:00'
      item.endsAt =
        this.$moment(this.endDate)
          .add(1, 'days')
          .format('YYYY-MM-DD') + ' 08:00:00'
      this.$emit('updateItem', item)
      this.$emit('updateStep', 2)
    }
  }
}
</script>
